"use client"

import * as React from "react"
import { CalendarIcon } from "lucide-react"

import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

function formatDate(date: Date | undefined) {
  if (!date) {
    return ""
  }

  // Extract calendar day components directly to avoid timezone conversion issues
  // This works correctly for both UTC dates (from database) and local dates (from Calendar)
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Use local date components to preserve the calendar day the date represents
  const year = date.getFullYear();
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  
  return `${month} ${String(day).padStart(2, '0')}, ${year}`;
}

interface DatePickerProps {
  date: Date | undefined
  setDate: (date: Date | undefined) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  disablePastDates?: boolean
  label?: string
}

export function DatePicker({ 
  date, 
  setDate, 
  placeholder = "Pick a date", 
  className, 
  disabled, 
  disablePastDates = false,
  label
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [month, setMonth] = React.useState<Date | undefined>(date)
  const [value, setValue] = React.useState(formatDate(date))

  // Update value when date prop changes
  React.useEffect(() => {
    setValue(formatDate(date))
    setMonth(date)
  }, [date])

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen(!open)
  }

  return (
    <div className="flex flex-col gap-3">
      {label && (
        <Label htmlFor="date" className="px-1">
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative flex items-center">
          <Input
            id="date"
            value={value}
            placeholder={placeholder}
            className={`bg-background pr-10 ${className || ''}`}
            disabled={disabled}
            readOnly
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault()
                setOpen(true)
              }
            }}
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute top-1/2 right-1 -translate-y-1/2 z-10 flex items-center justify-center h-7 w-7 rounded-md hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
              disabled={disabled}
              onClick={handleButtonClick}
            >
              <CalendarIcon className="h-4 w-4" />
              <span className="sr-only">Select date</span>
            </button>
          </PopoverTrigger>
        </div>
        <PopoverContent
          className="w-auto overflow-hidden p-0 z-50"
          align="end"
          alignOffset={-8}
          sideOffset={10}
        >
          <Calendar
            mode="single"
            selected={date}
            captionLayout="dropdown"
            month={month}
            onMonthChange={setMonth}
            fromYear={new Date().getFullYear() - 10}
            toYear={2030}
            disabled={disablePastDates ? (date) => date < new Date(new Date().setHours(0, 0, 0, 0)) : undefined}
            onSelect={(selectedDate) => {
              setDate(selectedDate)
              setValue(formatDate(selectedDate))
              setOpen(false)
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
